import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useFeaturedArticle } from "@/hooks/useArticles";
import { featuredArticle as mockFeatured } from "@/mocks/articles";

const heroImages = [
  "https://public.readdy.ai/ai/img_res/acc4c7038f5fa718e545276a1d9d8a6e.jpg",
  "https://readdy.ai/api/search-image?query=african%20woman%20entrepreneur%20tech%20startup%20Nairobi%20Kenya%20modern%20office%20confident%20leader%20innovation%20digital&width=1400&height=800&seq=hero-bg2&orientation=landscape",
  "https://readdy.ai/api/search-image?query=young%20african%20startup%20founders%20team%20coworking%20space%20Dakar%20Abidjan%20vibrant%20modern%20diverse%20entrepreneurs&width=1400&height=800&seq=hero-bg3&orientation=landscape",
];

export default function HeroSection() {
  const { t } = useTranslation();
  const { article: featured } = useFeaturedArticle();
  const [bgIndex, setBgIndex] = useState(0);
  const [fade, setFade] = useState(true);

  useEffect(() => {
    const interval = setInterval(() => {
      setFade(false);
      setTimeout(() => {
        setBgIndex((i) => (i + 1) % heroImages.length);
        setFade(true);
      }, 500);
    }, 6000);
    return () => clearInterval(interval);
  }, []);

  const article = featured ?? {
    id: mockFeatured.id,
    title: mockFeatured.title,
    image: mockFeatured.image,
    country: mockFeatured.country,
    countryFlag: mockFeatured.countryFlag,
    sector: mockFeatured.sector,
    readTime: mockFeatured.readTime ?? 8,
  };

  const quickLinks = [
    {
      icon: "ri-play-circle-line",
      labelKey: "hero.quickLinks.interviews.label",
      sublabelKey: "hero.quickLinks.interviews.sublabel",
      path: "/interviews",
      color: "from-red-500/80 to-red-600/80",
      iconColor: "text-red-300",
      badgeKey: "hero.quickLinks.interviews.badge",
      badgeColor: "bg-red-500",
    },
    {
      icon: "ri-compass-3-line",
      labelKey: "hero.quickLinks.opportunites.label",
      sublabelKey: "hero.quickLinks.opportunites.sublabel",
      path: "/opportunites",
      color: "from-emerald-500/80 to-emerald-600/80",
      iconColor: "text-emerald-300",
      badgeKey: "hero.quickLinks.opportunites.badge",
      badgeColor: "bg-emerald-500",
    },
    {
      icon: "ri-graduation-cap-line",
      labelKey: "hero.quickLinks.formations.label",
      sublabelKey: "hero.quickLinks.formations.sublabel",
      path: "/formations",
      color: "from-amber-500/80 to-orange-500/80",
      iconColor: "text-amber-300",
      badgeKey: "hero.quickLinks.formations.badge",
      badgeColor: "bg-orange-500",
    },
  ];

  const stats = [
    { icon: "ri-article-line", value: "500+", labelKey: "hero.stats.stories" },
    { icon: "ri-play-circle-line", value: "50+", labelKey: "hero.stats.interviews" },
    { icon: "ri-compass-3-line", value: "80+", labelKey: "hero.stats.opportunites" },
    { icon: "ri-graduation-cap-line", value: "20+", labelKey: "hero.stats.formations" },
    { icon: "ri-global-line", value: "54", labelKey: "hero.stats.pays" },
    { icon: "ri-team-line", value: "120K+", labelKey: "hero.stats.lecteurs" },
  ];

  return (
    <section className="relative w-full min-h-screen overflow-hidden flex flex-col">
      {/* Background */}
      <div className="absolute inset-0">
        <img
          src={heroImages[bgIndex]}
          alt="Wazi Stories Afrique"
          className={`w-full h-full object-cover object-top transition-opacity duration-700 ${fade ? "opacity-100" : "opacity-0"}`}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/60 to-black/30"></div>
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-black/20"></div>
      </div>

      {/* Main content */}
      <div className="relative z-10 flex-1 flex flex-col justify-center">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 w-full pt-24 md:pt-28 pb-8">
          <div className="max-w-3xl">

            {/* Tagline badge */}
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 text-white/90 text-xs font-semibold px-3 md:px-4 py-2 rounded-full mb-4 md:mb-6 uppercase tracking-widest">
              <i className="ri-earth-line text-orange-400"></i>
              <span className="hidden sm:inline">{t("hero.tagline")}</span>
              <span className="sm:hidden">{t("hero.taglineMobile")}</span>
            </div>

            {/* Main headline */}
            <h1 className="text-3xl sm:text-4xl md:text-6xl lg:text-7xl font-bold text-white leading-tight mb-3 md:mb-4 font-serif">
              {t("hero.headline1")}<br />
              <span className="text-orange-400">{t("hero.headline2")}</span>
            </h1>

            <p className="text-white/75 text-sm md:text-xl leading-relaxed mb-6 md:mb-8 max-w-xl">
              {t("hero.subtitle")}
            </p>

            {/* CTA buttons */}
            <div className="flex flex-wrap gap-3 md:gap-4 mb-8 md:mb-12">
              <Link
                to="/success-stories"
                className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-bold px-6 md:px-8 py-3 md:py-4 rounded-full transition-all duration-300 hover:scale-105 cursor-pointer whitespace-nowrap text-sm md:text-base"
              >
                <i className="ri-compass-3-line"></i>
                {t("hero.cta.explore")}
              </Link>
              <Link
                to="/interviews"
                className="inline-flex items-center gap-2 bg-white/15 hover:bg-white/25 backdrop-blur-sm text-white font-semibold px-5 md:px-6 py-3 md:py-4 rounded-full transition-all duration-300 cursor-pointer whitespace-nowrap border border-white/30 text-sm md:text-base"
              >
                <i className="ri-play-circle-line text-red-400"></i>
                {t("hero.cta.interviews")}
              </Link>
            </div>

            {/* Quick links */}
            <div className="flex flex-col sm:flex-row gap-3">
              {quickLinks.map((ql) => (
                <Link
                  key={ql.path}
                  to={ql.path}
                  className={`group flex items-center gap-3 bg-gradient-to-r ${ql.color} backdrop-blur-sm border border-white/20 rounded-2xl px-4 py-3 hover:scale-105 transition-all duration-300 cursor-pointer sm:flex-1`}
                >
                  <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-white/20 flex-shrink-0">
                    <i className={`${ql.icon} ${ql.iconColor} text-xl`}></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-white font-bold text-sm">{t(ql.labelKey)}</span>
                      <span className={`text-white text-xs font-bold px-1.5 py-0.5 rounded-full ${ql.badgeColor}`}>{t(ql.badgeKey)}</span>
                    </div>
                    <p className="text-white/60 text-xs">{t(ql.sublabelKey)}</p>
                  </div>
                  <i className="ri-arrow-right-line text-white/40 group-hover:text-white/80 group-hover:translate-x-1 transition-all flex-shrink-0"></i>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Featured article strip */}
      {article && (
        <div className="relative z-10 bg-black/50 backdrop-blur-sm border-t border-white/10">
          <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-3 md:py-4">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4">
              <div className="flex items-center gap-2 flex-shrink-0">
                <div className="w-2 h-2 rounded-full bg-orange-500 animate-pulse"></div>
                <span className="text-orange-400 text-xs font-bold uppercase tracking-widest whitespace-nowrap">{t("hero.featured.label")}</span>
              </div>
              <div className="flex-1 min-w-0 flex items-center gap-3 md:gap-4">
                <div className="w-10 h-10 md:w-12 md:h-12 rounded-xl overflow-hidden flex-shrink-0">
                  <img src={article.image} alt={article.title} className="w-full h-full object-cover object-top" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white font-semibold text-xs md:text-sm line-clamp-1">{article.title}</p>
                  <p className="text-white/50 text-xs">{article.countryFlag} {article.country} · {article.sector} · {article.readTime} min</p>
                </div>
              </div>
              <Link
                to={`/article/${article.id}`}
                className="flex-shrink-0 flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white text-xs font-bold px-4 py-2 rounded-full transition-colors cursor-pointer whitespace-nowrap"
              >
                {t("hero.featured.read")} <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* Stats bar */}
      <div className="relative z-10 bg-black/60 backdrop-blur-sm border-t border-white/10">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-3 md:py-4 overflow-x-auto">
          <div className="flex items-center gap-6 md:gap-14 min-w-max md:min-w-0 md:flex-wrap md:justify-center mx-auto">
            {stats.map((stat) => (
              <div key={stat.labelKey} className="flex items-center gap-2 text-white flex-shrink-0">
                <div className="w-7 h-7 flex items-center justify-center">
                  <i className={`${stat.icon} text-orange-400 text-lg`}></i>
                </div>
                <div>
                  <div className="text-sm md:text-base font-bold leading-none">{stat.value}</div>
                  <div className="text-xs text-white/50 whitespace-nowrap">{t(stat.labelKey)}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
